import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Mail, MailX, Loader2, Send, AlertCircle } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase B2.3 — Sends the FIRST admin invite for a tenant created via
 * the deferred-invite flow. The tenant must already exist (created via
 * `POST /super-admin/tenants`); this dialog hits the new
 * `POST /super-admin/tenants/{slug}/admin-invite` endpoint.
 *
 * 409s are surfaced as "An admin already exists for this tenant — use
 * the resend action on the pending invite instead." so SAs don't go in
 * circles.
 *
 * One-shot accept URL is shown inline on success — same idiom as the
 * legacy invite-success panel on `TenantsPage`.
 */

interface InviteResponse {
  tenant: { slug: string };
  invite: {
    token: string;
    expires_at: string;
    accept_url: string;
    email_sent: boolean;
    email_error: string | null;
  };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantSlug: string;
  tenantName: string;
}

export function SendAdminInviteDialog({ open, onOpenChange, tenantSlug, tenantName }: Props) {
  const qc = useQueryClient();
  const [email, setEmail] = React.useState("");
  const [fullName, setFullName] = React.useState("");
  const [result, setResult] = React.useState<InviteResponse["invite"] | null>(null);
  const [copied, setCopied] = React.useState(false);

  // Reset local state every time the dialog re-opens. A previous invite's
  // token must NEVER leak into the next session.
  React.useEffect(() => {
    if (open) {
      setResult(null);
      setEmail("");
      setFullName("");
      setCopied(false);
    }
  }, [open]);

  const inviteMutation = useMutation<
    InviteResponse,
    Error,
    { admin_email: string; admin_full_name: string }
  >({
    mutationFn: (input) =>
      api.post<InviteResponse>(`/super-admin/tenants/${tenantSlug}/admin-invite`, input),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["super-admin", "tenants"] });
      qc.invalidateQueries({ queryKey: ["super-admin", "pending-invites", tenantSlug] });
      setResult(data.invite);
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    inviteMutation.mutate({
      admin_email: email.trim().toLowerCase(),
      admin_full_name: fullName.trim(),
    });
  };

  const onCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.accept_url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // Best-effort; textarea below is selectable as fallback.
    }
  };

  const errorMsg = inviteMutation.error ? extractErrorMessage(inviteMutation.error) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{result ? "Invite sent" : `Send admin invite · ${tenantName}`}</DialogTitle>
          <DialogDescription>
            {result
              ? "Token shown only once — copy the link before closing."
              : "Creates the first admin user and emails the activation link. Use this once you've finished configuring the tenant's modules, forms, and branding."}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <SuccessBody invite={result} copied={copied} onCopy={onCopy} />
        ) : (
          <form id="send-admin-invite-form" onSubmit={onSubmit} className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="admin-name">Primary admin · full name</Label>
              <Input
                id="admin-name"
                required
                autoFocus
                maxLength={160}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Priya Sharma"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="admin-email">Primary admin · email</Label>
              <Input
                id="admin-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="head@institution.example.com"
                autoComplete="off"
              />
            </div>
            {errorMsg && (
              <div
                role="alert"
                className="border-destructive/50 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
              >
                <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span>{errorMsg}</span>
              </div>
            )}
          </form>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="send-admin-invite-form"
                disabled={inviteMutation.isPending || !email || !fullName}
              >
                {inviteMutation.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="size-4" aria-hidden="true" />
                    Send invite
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SuccessBody({
  invite,
  copied,
  onCopy,
}: {
  invite: InviteResponse["invite"];
  copied: boolean;
  onCopy: () => void;
}) {
  const expiresIn = humaniseExpiry(invite.expires_at);

  return (
    <div className="grid gap-3">
      {invite.email_sent ? (
        <div className="flex items-start gap-2.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-200">
          <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-semibold">Invite email sent.</p>
            <p className="opacity-90">
              Should arrive within a few minutes. Resend from the tenant detail if not.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200">
          <MailX className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-semibold">Email delivery didn&apos;t complete.</p>
            <p className="opacity-90">
              {invite.email_error ?? "Provider didn't confirm delivery."} The link below still works
              — copy it and forward manually.
            </p>
          </div>
        </div>
      )}
      <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/30 dark:bg-amber-950/30 dark:text-amber-200">
        <strong className="font-semibold">Token shown only once.</strong> Closing this dialog wipes
        it from memory.
      </div>
      <div className="grid gap-1.5">
        <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
          Invite link
        </p>
        <textarea
          readOnly
          value={invite.accept_url}
          className="bg-muted/40 min-h-[5.5rem] rounded-md border px-3 py-2 font-mono text-xs"
          onFocus={(e) => e.currentTarget.select()}
        />
        <p className="text-muted-foreground text-xs">Expires in {expiresIn}.</p>
      </div>
      <Button onClick={onCopy} className="w-fit">
        <Mail className="size-4" aria-hidden="true" />
        {copied ? "Copied!" : "Copy link"}
      </Button>
    </div>
  );
}

function humaniseExpiry(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return "less than a minute";
  const hours = Math.round(ms / 3_600_000);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"}`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"}`;
}

function extractErrorMessage(err: Error): string {
  if (!(err instanceof ApiError)) {
    return err.message ?? "Could not send the invite. Try again in a moment.";
  }
  switch (err.code) {
    case "CONFLICT":
      return "An admin already exists for this tenant. Use the resend action on the pending invite instead.";
    case "VALIDATION_ERROR":
      return err.message || "Some fields look off. Check the values and retry.";
    case "FORBIDDEN":
      return "Your account is not authorised to send invites.";
    case "UNAUTHORIZED":
      return "Your session expired. Sign in again and retry.";
    case "NOT_FOUND":
      return "Tenant not found. Refresh the page and try again.";
    default:
      return err.message || "Could not send the invite. Try again in a moment.";
  }
}
